import { useCallback, useEffect, useRef, useState } from 'react';
import { Label, TextArea, TextField } from '@heroui/react';

import UserBadge from './UserBadge';
import { MENTIONS } from '../../i18n/el';
import { activeMentionAt } from '../../../../api/lib/mentions.js';

// **Πεδίο κειμένου με υπόδειξη ονομάτων** (D44, ΛΑ-22, ΜΛΑ-11).
//
// ⚠ **ΤΟ ΚΡΙΤΗΡΙΟ «ΑΝΟΙΓΕΙ Ο ΚΑΤΑΛΟΓΟΣ;» ΕΙΝΑΙ ΤΟ ΙΔΙΟ ΜΕ ΤΟ «ΕΙΝΑΙ ΑΝΑΦΟΡΑ;».**
// Και τα δύο τα απαντά η `activeMentionAt` του `api/lib/mentions.js`, δηλαδή η
// **ίδια** συνάρτηση που ο διακομιστής χρησιμοποιεί για να αποφασίσει ποιος
// ειδοποιείται. Δεύτερο κριτήριο εδώ θα παρήγαγε επιλογέα που ανοίγει εκεί όπου ο
// διακομιστής δεν βλέπει αναφορά — συμπλήρωση που δεν μετράει — ή που σιωπά εκεί
// όπου βλέπει.
//
// ⚠ **Ο ΚΑΤΑΛΟΓΟΣ ΔΕΝ ΠΡΟΣΦΕΡΕΙ ΠΟΤΕ ΟΝΟΜΑ ΕΚΤΟΣ ΜΕΛΩΝ.** Δεν είναι φράχτης —
// ο φράχτης είναι το **422** του API — είναι το να μη φτάσει ποτέ ο χρήστης εκεί.
// Η αναφορά σε μη μέλος γεννιέται μόνο από πληκτρολόγηση με το χέρι.
//
// ─────────────────────────────────────────────────────────────────────────────
// ΓΙΑΤΙ ΚΑΘΑΡΟΣ ΚΑΤΑΛΟΓΟΣ ΚΑΙ ΟΧΙ ΤΟ ΑΝΑΔΥΟΜΕΝΟ ΤΗΣ ΒΙΒΛΙΟΘΗΚΗΣ
// ─────────────────────────────────────────────────────────────────────────────
//
// Τα αναδυόμενα της βιβλιοθήκης διεπαφής **μετακινούν την εστίαση** μέσα τους: το
// κάνουν σωστά, γιατί είναι φτιαγμένα για κουμπί που ανοίγει μενού. Εδώ ο χρήστης
// **πληκτρολογεί** — η εστίαση οφείλει να μείνει στο πεδίο, αλλιώς κάθε πάτημα
// βέλους διακόπτει τη γραφή. Είναι το πρότυπο `combobox` του WAI-ARIA: το πεδίο
// κρατά την εστίαση και δηλώνει με `aria-activedescendant` **ποιο** στοιχείο είναι
// ενεργό.
//
// *Απορρίφθηκε* το `Autocomplete` της βιβλιοθήκης: αντικαθιστά ολόκληρο το πεδίο
// και δουλεύει πάνω σε **μία τιμή**, ενώ εδώ ο κατάλογος αφορά **μια λέξη μέσα σε
// κείμενο πολλών γραμμών**, που μπορεί να έχει και δεύτερη αναφορά μετά.
//
// ⚠ **ΤΟ `Escape` ΣΤΑΜΑΤΑΕΙ ΕΔΩ.** Το πεδίο ζει μέσα σε διαλόγους· χωρίς
// `stopPropagation`, το κλείσιμο του καταλόγου θα έκλεινε **και τον διάλογο**, και
// ο χρήστης θα έχανε ό,τι είχε γράψει.

/** Πόσα ονόματα δείχνει ο κατάλογος. Πάνω από αυτά, ο χρήστης πληκτρολογεί. */
const MAX_SUGGESTIONS = 8;

/**
 * Ταιριάζει το ερώτημα με τα μέλη.
 *
 * Δύο κριτήρια, και το δεύτερο έχει **δηλωμένο όριο**: το όνομα χρήστη με
 * **πρόθεμα**, και το ονοματεπώνυμο με **υποσυμβολοσειρά σε πεζά**. Οι τόνοι
 * **δεν** εξομαλύνονται — το «αν» δεν βρίσκει την «Άννα».
 *
 * *Ο λόγος δεν είναι αμέλεια:* ο κανόνας εξομάλυνσης τονισμού υπάρχει ήδη στον
 * διακομιστή, για την αναζήτηση πόρων, και τρίτη διατύπωσή του εδώ θα ήταν
 * ακριβώς η υπογραφή που έχει κοστίσει πέντε φορές. Ο **στόχος** της αναφοράς
 * είναι πάντοτε το όνομα χρήστη, που κατά το σχήμα είναι λατινικοί χαρακτήρες:
 * το πρόθεμα το βρίσκει πάντοτε, και με **κενό** ερώτημα ο κατάλογος δείχνει
 * ούτως ή άλλως όλα τα μέλη.
 */
function matching(members, query) {
    if (!query) return members.slice(0, MAX_SUGGESTIONS);

    return members
        .filter(
            (member) =>
                String(member.username ?? '').startsWith(query) ||
                String(member.fullName ?? '').toLowerCase().includes(query)
        )
        .slice(0, MAX_SUGGESTIONS);
}

export default function MentionTextArea({
    value,
    onChange,
    members = [],
    // `true` όσο ο κατάλογος μελών δεν έχει φτάσει. **Ορατή αναμονή και όχι κενός
    // κατάλογος**: κενός κατάλογος διαβάζεται ως απάντηση «κανένα μέλος», και ο
    // χρήστης δεν μπορεί να τον ξεχωρίσει από «δεν ήρθε ακόμη» (D38).
    isLoadingMembers = false,
    /**
     * Καλείται τη **στιγμή** που ο χρήστης αρχίζει να γράφει αναφορά.
     *
     * Υπάρχει ώστε ο γονέας να ζητήσει τα μέλη **τότε** και όχι στη φόρτωση της
     * σελίδας. Χωρίς αυτό, ο γονέας θα έπρεπε να ρωτήσει μόνος του «γράφει
     * αναφορά;» — δηλαδή **δεύτερη διατύπωση** του κανόνα, στο σημείο ακριβώς που
     * ολόκληρη η D44 υπάρχει για να τη σταματήσει.
     */
    onMentionStart = null,
    // ⚠ **Χωρίς προεπιλογή, επίτηδες.** Πεδίο χωρίς ετικέτα είναι απρόσιτο σε
    // όποιον ακούει την οθόνη (ΜΛΑ-11), και μια προεπιλογή θα έκρυβε την
    // παράλειψη αντί να τη δείξει: ο στατικός έλεγχος καλωδίωσης απαιτεί κάθε
    // σημείο χρήσης να τη δίνει ρητά.
    //
    // *Σημείωση για όποιον προσθέσει σημείο χρήσης:* ο έλεγχος διαβάζει τα
    // χαρακτηριστικά JSX **ως κείμενο μέχρι το πρώτο `>`**, και το ενσωματωμένο
    // βέλος (`() =>`) περιέχει ένα. Το `label` δηλώνεται επομένως **πριν** από
    // κάθε βέλος, αλλιώς ο φράχτης το αναφέρει ως λείπον ενώ δίνεται.
    label,
    isLabelHidden = false,
    placeholder = '',
    rows = 2,
    ...fieldProps
}) {
    const containerRef = useRef(null);
    const [active, setActive] = useState(null);
    const [highlighted, setHighlighted] = useState(0);

    // ⚠ **Το πεδίο εντοπίζεται από τον περιέκτη και όχι με προωθημένη αναφορά.**
    // Η βιβλιοθήκη μπορεί να τυλίγει το `<textarea>` σε δικά της στοιχεία, και το
    // αν προωθεί αναφορά είναι **υπόθεση για τη συμπεριφορά της** — ακριβώς η
    // κατηγορία Κ. Η αναζήτηση στο δέντρο δεν υποθέτει τίποτα.
    const fieldOf = () => containerRef.current?.querySelector('textarea') ?? null;

    /** Ξαναδιαβάζει τη θέση του δρομέα και αποφασίζει αν ο κατάλογος ανοίγει. */
    const sync = useCallback(() => {
        const field = fieldOf();
        if (!field) return;

        const found = activeMentionAt(field.value, field.selectionStart);
        setActive(found);
        setHighlighted(0);
        if (found) onMentionStart?.();
    }, [onMentionStart]);

    // Ο δρομέας μετακινείται και χωρίς πληκτρολόγηση — με ποντίκι, με επικόλληση,
    // με αναίρεση. Το `selectionchange` του ίδιου του πεδίου τα καλύπτει όλα.
    useEffect(() => {
        const field = fieldOf();
        if (!field) return undefined;

        field.addEventListener('selectionchange', sync);
        return () => field.removeEventListener('selectionchange', sync);
    }, [sync]);

    const options = matching(members, active?.query ?? '');
    const isOpen = Boolean(active) && (isLoadingMembers || options.length > 0);

    /** Αντικαθιστά τη λέξη που πληκτρολογείται με το πλήρες όνομα. */
    function accept(member) {
        const field = fieldOf();
        if (!field || !active) return;

        const before = value.slice(0, active.start);
        const after = value.slice(active.end);
        // Το κενό μετά το όνομα δεν είναι καλλωπισμός: χωρίς αυτό ο δρομέας μένει
        // κολλητά στο όνομα, ο κατάλογος **ξανανοίγει** για την ίδια λέξη, και ο
        // χρήστης πρέπει να πατήσει `Escape` για να συνεχίσει να γράφει.
        const inserted = `@${member.username} `;

        onChange(`${before}${inserted}${after}`);
        setActive(null);

        // Η θέση του δρομέα ορίζεται μετά την απόδοση: αλλιώς η νέα τιμή δεν έχει
        // φτάσει ακόμη στο πεδίο και η θέση υπολογίζεται πάνω στο παλιό κείμενο.
        const caret = active.start + inserted.length;
        requestAnimationFrame(() => {
            const target = fieldOf();
            target?.focus();
            target?.setSelectionRange(caret, caret);
        });
    }

    function onKeyDown(event) {
        if (!isOpen) return;

        if (event.key === 'Escape') {
            // ⚠ Χωρίς αυτό, το `Escape` φτάνει στον διάλογο που φιλοξενεί το πεδίο
            // και κλείνει **εκείνον**, παίρνοντας μαζί ό,τι έχει γράψει ο χρήστης.
            event.preventDefault();
            event.stopPropagation();
            setActive(null);
            return;
        }

        if (options.length === 0) return;

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            // Κυκλικά: σε κατάλογο οκτώ ονομάτων, το «τέρμα» δεν προσφέρει τίποτα
            // και υποχρεώνει σε αλλαγή κατεύθυνσης.
            setHighlighted((current) => (current + step + options.length) % options.length);
            return;
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            accept(options[highlighted]);
        }
    }

    const listId = 'mention-options';
    const activeId = isOpen && options.length > 0 ? `${listId}-${highlighted}` : undefined;

    return (
        <div className="relative" ref={containerRef}>
            <TextField value={value} onChange={onChange} {...fieldProps}>
                <Label className={isLabelHidden ? 'sr-only' : ''}>{label}</Label>
                <TextArea
                    rows={rows}
                    placeholder={placeholder}
                    onKeyDown={onKeyDown}
                    onKeyUp={sync}
                    onClick={sync}
                    onFocus={sync}
                    onBlur={() => setActive(null)}
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? listId : undefined}
                    aria-activedescendant={activeId}
                    aria-autocomplete="list"
                />
            </TextField>

            {isOpen && (
                <ul
                    id={listId}
                    role="listbox"
                    aria-label={MENTIONS.listLabel}
                    // Απόλυτη θέση **κάτω** από το πεδίο, με δικό της βάθος: μέσα σε
                    // περιοχή με κύλιση ο κατάλογος πρέπει να μπορεί να ξεπεράσει το
                    // όριό της, αλλιώς κόβεται στη μέση.
                    className="absolute inset-x-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-xl border border-border bg-surface p-1 shadow-lg"
                >
                    {isLoadingMembers ? (
                        <li className="px-2 py-1.5 text-sm text-muted">{MENTIONS.loading}</li>
                    ) : (
                        options.map((member, index) => (
                            <li
                                key={member.id ?? member.username}
                                id={`${listId}-${index}`}
                                role="option"
                                aria-selected={index === highlighted}
                                // `onMouseDown` και όχι `onClick`: το `blur` του
                                // πεδίου προηγείται του `click` και θα είχε ήδη
                                // κλείσει τον κατάλογο πριν φτάσει το πάτημα.
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    accept(member);
                                }}
                                onMouseEnter={() => setHighlighted(index)}
                                className={[
                                    'flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5',
                                    index === highlighted ? 'bg-accent-soft' : ''
                                ].join(' ')}
                            >
                                <UserBadge user={member} />
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
}
